import React, { useState, useEffect } from 'react';
import { pushAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function PushNotificationToggle() {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      setLoading(false);
      return;
    }
    setSupported(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
    setLoading(false);
  };

  const handleToggle = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (subscribed) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await pushAPI.unsubscribe(sub.toJSON());
          await sub.unsubscribe();
        }
        setSubscribed(false);
        toast.success('Push notifications disabled');
      } else {
        const keyRes = await pushAPI.getVapidKey();
        const publicKey = keyRes.data.public_key;
        if (!publicKey) {
          toast.error('Push notifications not configured on server');
          setLoading(false);
          return;
        }
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        await pushAPI.subscribe(sub.toJSON());
        setSubscribed(true);
        toast.success('Push notifications enabled!');
      }
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to toggle push notifications');
      }
    }
    setLoading(false);
  };

  const handleTest = async () => {
    try {
      await pushAPI.test();
      toast.success('Test notification sent');
    } catch {
      toast.error('Failed to send test');
    }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-2" data-testid="push-notification-toggle">
      <Button
        variant={subscribed ? 'default' : 'outline'}
        size="sm"
        onClick={handleToggle}
        disabled={loading}
        className="gap-1.5"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> :
          subscribed ? <BellRing size={14} /> : <BellOff size={14} />}
        {subscribed ? 'Push On' : 'Enable Push'}
      </Button>
      {subscribed && (
        <Button variant="ghost" size="sm" onClick={handleTest} className="gap-1.5 text-xs">
          <Bell size={14} /> Test
        </Button>
      )}
    </div>
  );
}
